import React from 'react';
import PropTypes from 'prop-types';
import {
    AlertCircle,
    ArrowUpToLine,
    CheckCircle2,
    HelpCircle,
    MinusCircle,
    RefreshCw,
} from 'lucide-react';
import Chip from '../ui/Chip';
import Tooltip from '../ui/Tooltip';
import { describeRuleResult } from './optimizerRuleRegistry';

// "Within limit" means the optimized value satisfies this one rule within
// tolerance. It does not certify other rules or global optimality.
const STATUS = Object.freeze({
    within: { label: 'Within limit', tone: 'success', Icon: CheckCircle2 },
    at_limit: { label: 'At limit', tone: 'info', Icon: ArrowUpToLine },
    exceeds: { label: 'Exceeds', tone: 'danger', Icon: AlertCircle },
    not_applied: { label: 'Not applied', tone: 'neutral', Icon: MinusCircle },
    unavailable: { label: 'Unavailable', tone: 'neutral', Icon: HelpCircle },
});
const RERUN = Object.freeze({ label: 'Rerun to check', tone: 'warning', Icon: RefreshCw });

const STATUS_MEANING = Object.freeze({
    within: 'The optimized value satisfies this limit.',
    at_limit: 'The optimized value sits on this limit, so the limit is shaping the result.',
    exceeds: 'The optimized value breaks this limit.',
    not_applied: 'This limit was switched off for the run.',
    unavailable: 'This value could not be evaluated for the run.',
});

export default function RuleStatusChip({ evaluation, rule, changed = false, stale = false }) {
    if (!evaluation?.configured) return null;
    const presentation = changed ? RERUN : STATUS[evaluation.status] ?? STATUS.unavailable;
    const description = describeRuleResult(evaluation, rule);
    const tooltip = changed
        ? 'This limit was edited after the run. Rerun to evaluate it.'
        : [
            stale ? 'From the previous run; the configuration has changed since.' : null,
            STATUS_MEANING[evaluation.status] ?? null,
            description?.headroom ?? null,
        ].filter(Boolean).join(' ');
    const faded = stale && !changed;
    const { Icon } = presentation;
    return (
        <Tooltip content={tooltip}>
            <Chip
                icon={<Icon strokeWidth={2.25} />}
                label={presentation.label}
                tone={faded ? 'neutral' : presentation.tone}
                className={faded ? 'opacity-70' : undefined}
            />
        </Tooltip>
    );
}

RuleStatusChip.propTypes = {
    evaluation: PropTypes.shape({
        configured: PropTypes.bool,
        status: PropTypes.string,
    }),
    rule: PropTypes.shape({
        unit: PropTypes.string.isRequired,
        form: PropTypes.string.isRequired,
    }).isRequired,
    changed: PropTypes.bool,
    stale: PropTypes.bool,
};
